import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import FileV2 from './file-v2.model';
import { FileV2Service } from './file-v2.service';

const CONTENT_URL = /\/api\/FileV2\/(\d+)\/content/i;
const TOKEN = /[?&]token=\d+\.(\d+)\./;

/**
 * Extracts the FileV2 id from an API content URL, signed or not; null for any other URL.
 */
export function contentFileId(url: string | null | undefined): number | null {
  const match = url ? CONTENT_URL.exec(url) : null;
  return match ? Number(match[1]) : null;
}

/**
 * Reads the expiry (unix seconds) out of a signed content URL; null when the URL carries no token.
 */
export function contentTokenExpiry(url: string | null | undefined): number | null {
  const match = url ? TOKEN.exec(url) : null;
  return match ? Number(match[1]) : null;
}

/**
 * Recovers images whose signed content URL has expired.
 * Rich text and catalog data are cached in the client stores for the whole session, while the signed URLs
 * inside them live for minutes. An <img> cannot follow the API's redirect to the viewer the way a link can,
 * so this service listens for image load errors anywhere in the document, asks the API for a fresh FileV2
 * record and swaps the new signed URL into the element. Fresh URLs are cached until shortly before expiry.
 */
@Injectable({ providedIn: 'root' })
export class FileContentRefreshService {

  private started = false;
  private readonly fresh = new Map<number, string>();
  private readonly pending = new Map<number, Promise<string | null>>();

  constructor(private fileV2Service: FileV2Service) { }

  /**
   * Installs the document-level listener once. Error events do not bubble, so the capture phase is used.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    document.addEventListener('error', event => this.onError(event), true);
  }

  private onError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (!img || img.tagName !== 'IMG') return;
    const id = contentFileId(img.getAttribute('src'));
    // One retry per element; a second failure means the file itself is gone.
    if (!id || img.dataset['fileRetried']) return;
    img.dataset['fileRetried'] = '1';
    this.freshUrl(id).then(url => { if (url) img.src = url; });
  }

  /**
   * Returns a signed URL for the file that is still valid, from cache when possible.
   */
  private freshUrl(id: number): Promise<string | null> {
    const cached = this.fresh.get(id);
    if (cached && this.isUsable(cached)) return Promise.resolve(cached);

    let request = this.pending.get(id);
    if (!request) {
      request = firstValueFrom(this.fileV2Service.obtainFile(id))
        .then((file: FileV2) => {
          if (file?.url) this.fresh.set(id, file.url);
          return file?.url ?? null;
        })
        .catch(() => null)
        .finally(() => this.pending.delete(id));
      this.pending.set(id, request);
    }
    return request;
  }

  private isUsable(url: string): boolean {
    const expiry = contentTokenExpiry(url);
    return expiry !== null && expiry - Date.now() / 1000 > 30;
  }
}
